/* Speelsnuit theme – vanilla JS, no dependencies */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ───────────── Helpers ───────────── */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const formatMoney = (cents) => {
    const format = theme.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = Number(cents) || 0;
    const fmt = (amount, precision, thousands, decimal) => {
      const fixed = (amount / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      const intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intFormatted + decimal + dec : intFormatted;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2, ',', '.');
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_period_and_space_separator': return fmt(value, 2, ' ', '.');
        default: return fmt(value, 2, '.', ',');
      }
    });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const storage = {
    get(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) { /* ignore */ } }
  };

  /* ───────────── Scroll lock & focus trap ───────────── */
  let lockCount = 0;
  const lockScroll = () => {
    if (lockCount === 0) {
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbar}px`);
      document.documentElement.classList.add('is-locked');
    }
    lockCount += 1;
  };
  const unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove('is-locked');
  };

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const trapTab = (container, event) => {
    const items = $$(focusableSelector, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ───────────── Drawers ───────────── */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab' && this.classList.contains('is-open')) trapTab(this.panel, event);
      });
    }

    get isOpen() { return this.classList.contains('is-open'); }

    open(trigger) {
      if (this.isOpen) return;
      document.querySelectorAll('[data-drawer].is-open').forEach((d) => { if (d !== this && d.close) d.close(false); });
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      lockScroll();
      $$(`[aria-controls="${this.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      window.setTimeout(() => this.panel && this.panel.focus({ preventScroll: true }), 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      unlockScroll();
      $$(`[aria-controls="${this.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', class extends DrawerElement {});

  /* ───────────── Predictive search ───────────── */
  if (!customElements.get('search-modal')) {
    customElements.define('search-modal', class extends DrawerElement {
      connectedCallback() {
        super.connectedCallback();
        this.input = $('[data-predictive-input]', this);
        this.results = $('[data-predictive-results]', this);
        if (!this.input || !this.results) return;
        this.input.addEventListener('input', debounce(() => this.search(), 260));
      }

      open(trigger) {
        super.open(trigger);
        window.setTimeout(() => this.input && this.input.focus(), 80);
      }

      async search() {
        const q = this.input.value.trim();
        if (this.controller) this.controller.abort();
        if (q.length < 2) {
          this.results.innerHTML = '';
          return;
        }
        this.controller = new AbortController();
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
        try {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(response.status);
          const html = await response.text();
          const section = parseHTML(html).querySelector('#shopify-section-predictive-search');
          this.results.innerHTML = section ? section.innerHTML : '';
        } catch (error) {
          if (error.name !== 'AbortError') this.results.innerHTML = '';
        }
      }
    });
  }

  /* ───────────── Cart ───────────── */
  const Cart = {
    sections() {
      const list = ['cart-drawer', 'cart-icon-bubble'];
      const page = $('[data-cart-page]');
      if (page && page.dataset.sectionId) list.push(page.dataset.sectionId);
      return list;
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sections(), sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const message = data.description || data.message || theme.strings.error;
        throw new Error(message);
      }
      return data;
    },

    add(items) { return this.request(theme.routes.cartAdd, { items }).then((data) => { this.render(data.sections); return data; }); },
    change(line, quantity) { return this.request(theme.routes.cartChange, { line, quantity }).then((data) => { this.render(data.sections); return data; }); },

    render(sections) {
      if (!sections) return;
      const drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        const fresh = parseHTML(drawerHTML).querySelector('#CartDrawerContent');
        const current = document.getElementById('CartDrawerContent');
        if (fresh && current) current.replaceWith(fresh);
      }
      const bubbleHTML = sections['cart-icon-bubble'];
      if (bubbleHTML) {
        const fresh = parseHTML(bubbleHTML).querySelector('.cart-bubble');
        const target = document.getElementById('CartBubble');
        if (fresh && target) target.innerHTML = fresh.outerHTML;
      }
      const page = $('[data-cart-page]');
      if (page && page.dataset.sectionId && sections[page.dataset.sectionId]) {
        const fresh = parseHTML(sections[page.dataset.sectionId]).querySelector('[data-cart-page]');
        if (fresh) page.replaceWith(fresh);
      }
      CartTimer.start();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  };

  const CartTimer = {
    start() {
      const minutes = Number(theme.cartTimer) || 0;
      const el = $('[data-cart-timer]');
      if (!minutes || !el) return;
      let started = Number(storage.get('speelsnuit:cart-timer'));
      const now = Date.now();
      if (!started || now - started > minutes * 60000) {
        started = now;
        storage.set('speelsnuit:cart-timer', String(started));
      }
      clearInterval(this.interval);
      const tick = () => {
        const target = $('[data-cart-timer]');
        if (!target) return;
        const left = Math.max(0, minutes * 60000 - (Date.now() - started));
        const m = String(Math.floor(left / 60000)).padStart(2, '0');
        const s = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
        target.textContent = `${m}:${s}`;
        if (left <= 0) clearInterval(this.interval);
      };
      tick();
      this.interval = setInterval(tick, 1000);
    }
  };

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', class extends DrawerElement {
    open(trigger) {
      super.open(trigger);
      CartTimer.start();
    }
  });

  if (!customElements.get('cart-items')) {
    customElements.define('cart-items', class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const change = event.target.closest('[data-qty-change]');
          const remove = event.target.closest('[data-line-remove]');
          if (change) this.update(Number(change.dataset.qtyChange), Number(change.dataset.qtyValue));
          if (remove) this.update(Number(remove.dataset.lineRemove), 0);
        });
        this.addEventListener('change', (event) => {
          const input = event.target.closest('[data-qty-input]');
          if (input) this.update(Number(input.dataset.qtyInput), Math.max(0, parseInt(input.value, 10) || 0));
        });
      }

      async update(line, quantity) {
        const row = $(`.cart-line[data-line="${line}"]`, this);
        if (row) row.classList.add('is-loading');
        try {
          await Cart.change(line, quantity);
        } catch (error) {
          if (row) row.classList.remove('is-loading');
          window.alert(error.message);
        }
      }
    });
  }

  /* Quick add (product cards, upsells) */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-quick-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Number(data.get('quantity')) || 1;
    if (button) button.classList.add('is-loading');
    try {
      await Cart.add([{ id, quantity }]);
      if (theme.cartType === 'drawer' && $('cart-drawer')) {
        $('cart-drawer').open(button);
      } else {
        window.location.href = theme.routes.cart;
      }
    } catch (error) {
      window.alert(error.message);
    } finally {
      if (button) button.classList.remove('is-loading');
    }
  });

  /* ───────────── Product page ───────────── */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('product-info') || document;
        const json = $('[data-product-json]', this.root);
        if (!json) return;
        this.product = JSON.parse(json.textContent);
        this.form = document.getElementById(this.dataset.formId);
        this.tiers = $$('[data-tier]', this);
        this.picker = $('[data-variant-picker]', this);
        this.gallery = $('media-gallery', this.root);
        this.stickyPrice = $('[data-sticky-price]', this.root);
        if (this.form) {
          this.idInput = $('[data-variant-id]', this.form);
          this.qtyInput = $('[data-quantity]', this.form);
          this.button = $('[data-add-button]', this.form);
          this.label = $('[data-add-label]', this.form);
          this.error = $('[data-form-error]', this.form);
          this.form.addEventListener('submit', (event) => this.onSubmit(event));
        }
        this.addEventListener('change', (event) => this.onChange(event));
        const stickyAdd = $('[data-sticky-add]', this.root);
        if (stickyAdd && this.form) {
          stickyAdd.addEventListener('click', () => {
            if (this.form.requestSubmit) this.form.requestSubmit();
            else this.form.dispatchEvent(new Event('submit', { cancelable: true }));
          });
        }
        this.lastVariantId = null;
        this.update();
        this.initSticky();
      }

      get activeTier() {
        return this.tiers.find((tier) => { const input = $('.tier__input', tier); return input && input.checked; }) || this.tiers[0];
      }

      onChange(event) {
        if (event.target.matches('.tier__input')) {
          this.tiers.forEach((tier) => tier.classList.toggle('is-selected', tier.contains(event.target)));
        }
        this.update(event.target);
      }

      findVariant(values) {
        return this.product.variants.find((variant) => variant.options.every((option, index) => option === values[index]));
      }

      valuesFrom(container) {
        return $$('fieldset[data-option-index]', container).map((fieldset) => {
          const checked = $('input:checked', fieldset);
          return checked ? checked.value : null;
        });
      }

      baseVariant() {
        if (this.picker) return this.findVariant(this.valuesFrom(this.picker)) || null;
        const id = this.idInput ? Number(this.idInput.value) : null;
        return this.product.variants.find((variant) => variant.id === id) || this.product.variants[0];
      }

      unitsFor(tier) {
        if (!tier) return [this.baseVariant()];
        const units = $$('[data-unit]', tier);
        if (units.length) return units.map((unit) => this.findVariant(this.valuesFrom(unit)) || null);
        const qty = Number(tier.dataset.qty) || 1;
        const variant = this.baseVariant();
        return Array.from({ length: qty }, () => variant);
      }

      tierPrice(tier, units) {
        const base = units.reduce((sum, v) => sum + (v ? v.price : 0), 0);
        const value = parseFloat(tier.dataset.discountValue) || 0;
        let discount = 0;
        if (tier.dataset.discountType === 'amount') discount = Math.round(value * 100);
        if (tier.dataset.discountType === 'percentage') discount = Math.round((base * value) / 100);
        const compare = units.reduce((sum, v) => sum + (v ? Math.max(v.compare_at_price || 0, v.price) : 0), 0);
        return { price: Math.max(0, base - discount), compare };
      }

      update(changedInput) {
        const active = this.activeTier;
        const units = this.unitsFor(active);

        this.tiers.forEach((tier) => {
          const qty = Number(tier.dataset.qty) || 1;
          const tierUnits = tier === active ? units : Array.from({ length: qty }, () => units[0] || this.product.variants[0]);
          const { price, compare } = this.tierPrice(tier, tierUnits);
          const priceEl = $('[data-tier-price]', tier);
          if (priceEl) priceEl.textContent = formatMoney(price);
          const compareEl = $('[data-tier-compare]', tier);
          if (compareEl && !tier.dataset.compareOverride) compareEl.textContent = compare > price ? formatMoney(compare) : '';
          if (tier === active) this.activePrice = price;
        });

        const complete = units.length > 0 && units.every(Boolean);
        const available = complete && units.every((v) => v.available);

        if (!this.tiers.length) {
          const variant = units[0];
          this.activePrice = variant ? variant.price : 0;
          this.updatePriceBlock(variant);
          if (variant && this.picker) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
            $$('[data-option-value]', this.picker).forEach((el, i) => { el.textContent = variant.options[i]; });
          }
        }

        if (this.idInput && units[0]) this.idInput.value = units[0].id;
        if (this.qtyInput) this.qtyInput.value = String(units.length || 1);

        if (this.button) {
          this.button.disabled = !available;
          if (this.label) {
            if (!complete) this.label.textContent = theme.strings.unavailable;
            else if (!available) this.label.textContent = theme.strings.soldOut;
            else this.label.textContent = this.label.dataset.default || theme.strings.addToCart;
          }
        }

        if (this.stickyPrice && typeof this.activePrice === 'number') this.stickyPrice.textContent = formatMoney(this.activePrice);

        const changedUnit = changedInput && changedInput.closest ? changedInput.closest('[data-unit], [data-variant-picker]') : null;
        if (changedUnit && this.gallery) {
          const variant = this.findVariant(this.valuesFrom(changedUnit));
          if (variant && variant.featured_media_id && variant.id !== this.lastVariantId) {
            this.gallery.showMedia(variant.featured_media_id);
            this.lastVariantId = variant.id;
          }
        }
      }

      updatePriceBlock(variant) {
        const block = $('[data-price-block]', this.root);
        if (!block || !variant) return;
        const current = $('.price__current', block);
        const compare = $('.price__compare', block);
        if (current) current.lastChild.textContent = formatMoney(variant.price);
        if (compare) compare.lastChild.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
      }

      async onSubmit(event) {
        event.preventDefault();
        if (!this.button || this.button.disabled) return;
        const units = this.unitsFor(this.activeTier).filter(Boolean);
        const counts = new Map();
        units.forEach((variant) => counts.set(variant.id, (counts.get(variant.id) || 0) + 1));
        const properties = {};
        new FormData(this.form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });
        const items = Array.from(counts, ([id, quantity]) => {
          const item = { id, quantity };
          if (Object.keys(properties).length) item.properties = properties;
          return item;
        });

        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-busy', 'true');
        if (this.error) this.error.hidden = true;
        try {
          await Cart.add(items);
          if (theme.cartType === 'drawer' && $('cart-drawer')) {
            $('cart-drawer').open(this.button);
          } else {
            window.location.href = theme.routes.cart;
          }
        } catch (error) {
          if (this.error) {
            this.error.textContent = error.message;
            this.error.hidden = false;
          }
        } finally {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('aria-busy');
        }
      }

      initSticky() {
        const bar = $('[data-sticky-atc]', this.root);
        if (!bar || !this.button || !('IntersectionObserver' in window)) return;
        const footer = $('.footer');
        let pastButton = false;
        let footerVisible = false;
        const sync = () => {
          const show = pastButton && !footerVisible;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
          $('[data-sticky-add]', bar).tabIndex = show ? 0 : -1;
          document.body.classList.toggle('has-sticky-atc', show);
        };
        new IntersectionObserver(([entry]) => {
          pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sync();
        }).observe(this.button);
        if (footer) {
          new IntersectionObserver(([entry]) => {
            footerVisible = entry.isIntersecting;
            sync();
          }).observe(footer);
        }
      }
    });
  }

  if (!customElements.get('product-info')) customElements.define('product-info', class extends HTMLElement {});

  /* ───────────── Media gallery ───────────── */
  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', class extends HTMLElement {
      connectedCallback() {
        this.track = $('[data-gallery-track]', this);
        if (!this.track) return;
        this.slides = $$('.gallery__slide', this);
        this.thumbs = $$('[data-gallery-thumb]', this);
        this.dots = $$('.gallery__dot', this);
        this.thumbList = $('.gallery__thumbs', this);
        this.index = 0;
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryThumb))));
        const prev = $('[data-gallery-prev]', this);
        const next = $('[data-gallery-next]', this);
        if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
        if (next) next.addEventListener('click', () => this.go(this.index + 1));
        let ticking = false;
        this.track.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            const width = this.track.clientWidth || 1;
            this.setActive(Math.round(this.track.scrollLeft / width));
            ticking = false;
          });
        }, { passive: true });
      }

      go(index) {
        if (!this.slides.length) return;
        const target = (index + this.slides.length) % this.slides.length;
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
        this.setActive(target);
      }

      setActive(index) {
        if (index === this.index && this.dataset.init) return;
        this.dataset.init = 'true';
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        this.thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === this.index);
          if (i === this.index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
        const active = this.thumbs[this.index];
        if (active && this.thumbList) {
          const listRect = this.thumbList.getBoundingClientRect();
          const rect = active.getBoundingClientRect();
          if (rect.left < listRect.left || rect.right > listRect.right) {
            this.thumbList.scrollBy({ left: rect.left - listRect.left - listRect.width / 2 + rect.width / 2, behavior: 'smooth' });
          }
        }
        this.slides.forEach((slide, i) => {
          const video = $('video', slide);
          if (video && i !== this.index) video.pause();
        });
      }

      showMedia(id) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
        if (index >= 0) this.go(index);
      }
    });
  }

  /* ───────────── Generic slider ───────────── */
  if (!customElements.get('slider-carousel')) {
    customElements.define('slider-carousel', class extends HTMLElement {
      connectedCallback() {
        this.track = $('[data-slider-track]', this);
        if (!this.track) return;
        this.prev = $('[data-slider-prev]', this);
        this.next = $('[data-slider-next]', this);
        this.progress = $('[data-slider-progress]', this);
        if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
        if (this.next) this.next.addEventListener('click', () => this.step(1));
        this.track.addEventListener('scroll', debounce(() => this.update(), 30), { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
        this.update();
      }

      step(direction) {
        const slide = $('.slider__slide', this.track);
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const amount = slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.track.scrollBy({ left: direction * amount, behavior: reducedMotion ? 'auto' : 'smooth' });
      }

      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const isStatic = max <= 4;
        this.classList.toggle('is-static', isStatic);
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max - 4;
        if (this.progress) {
          const visible = this.track.clientWidth / (this.track.scrollWidth || 1);
          const ratio = max > 0 ? this.track.scrollLeft / max : 1;
          const width = Math.min(1, visible + ratio * (1 - visible));
          this.progress.style.width = `${(width * 100).toFixed(2)}%`;
        }
      }
    });
  }

  /* ───────────── Lazy video ───────────── */
  if (!customElements.get('lazy-video')) {
    customElements.define('lazy-video', class extends HTMLElement {
      connectedCallback() {
        this.video = $('video', this);
        if (!this.video) return;
        const sound = $('[data-video-sound]', this);
        if (sound) {
          sound.addEventListener('click', () => {
            this.video.muted = !this.video.muted;
            this.classList.toggle('is-unmuted', !this.video.muted);
            if (!this.video.muted) {
              $$('lazy-video.is-unmuted').forEach((other) => {
                if (other !== this && other.video) {
                  other.video.muted = true;
                  other.classList.remove('is-unmuted');
                }
              });
            }
          });
        }
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.load();
              if (!reducedMotion) this.video.play().catch(() => {});
            } else {
              this.video.pause();
            }
          });
        }, { rootMargin: '200px 0px', threshold: 0.15 }).observe(this);
      }

      load() {
        if (!this.video.src && this.video.dataset.src) {
          this.video.src = this.video.dataset.src;
          this.video.load();
        }
      }
    });
  }

  /* ───────────── Product recommendations ───────────── */
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded || !this.dataset.url) return;
        const load = async () => {
          this.dataset.loaded = 'true';
          try {
            const response = await fetch(this.dataset.url);
            const html = await response.text();
            const fresh = parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) {
              this.innerHTML = fresh.innerHTML;
              initReveal(this);
            }
          } catch (error) { /* ignore */ }
        };
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
              observer.disconnect();
              load();
            }
          }, { rootMargin: '600px 0px' });
          observer.observe(this);
        } else {
          load();
        }
      }
    });
  }

  /* ───────────── Sticky header ───────────── */
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.sticky !== 'true') return;
        let lastY = window.scrollY;
        const onScroll = () => {
          const y = window.scrollY;
          this.classList.toggle('is-scrolled', y > 8);
          const goingDown = y > lastY && y > 320;
          const menuOpen = document.documentElement.classList.contains('is-locked');
          this.classList.toggle('is-hidden', goingDown && !menuOpen);
          lastY = y;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();

        $$('[data-hover-dropdown]', this).forEach((details) => {
          details.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) details.open = true; });
          details.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) details.open = false; });
        });
      }
    });
  }

  /* ───────────── Facets ───────────── */
  if (!customElements.get('facet-filters')) {
    customElements.define('facet-filters', class extends HTMLElement {
      connectedCallback() {
        const aside = $('.facets', this);
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-facets-open]') && aside) {
            aside.classList.add('is-open');
            lockScroll();
          }
          if (event.target.closest('[data-facets-close]') && aside && aside.classList.contains('is-open')) {
            aside.classList.remove('is-open');
            unlockScroll();
          }
        });
        const submit = (form) => {
          const params = new URLSearchParams(new FormData(form));
          const sortForm = $('[data-sort-form]', this);
          if (form !== sortForm && sortForm) {
            const sort = $('select[name="sort_by"]', sortForm);
            if (sort) params.set('sort_by', sort.value);
          }
          [...params.keys()].forEach((key) => { if (params.get(key) === '') params.delete(key); });
          window.location.search = params.toString();
        };
        const debounced = debounce(submit, 700);
        this.addEventListener('change', (event) => {
          const input = event.target.closest('[data-autosubmit]');
          if (!input) return;
          const form = input.form;
          if (!form) return;
          if (input.type === 'number') debounced(form);
          else submit(form);
        });
      }
    });
  }

  /* Localization auto-submit */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('form[data-localization] [data-autosubmit]');
    if (select) select.form.submit();
  });

  /* ───────────── Reviews ───────────── */
  const initReviews = (scope = document) => {
    $$('[data-reviews]', scope).forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const toggle = $('[data-review-toggle]', section);
      const formWrap = toggle ? document.getElementById(toggle.getAttribute('aria-controls')) : null;
      if (toggle && formWrap) {
        toggle.addEventListener('click', () => {
          formWrap.hidden = !formWrap.hidden;
          toggle.setAttribute('aria-expanded', String(!formWrap.hidden));
        });
        if (window.location.hash === '#ReviewForm' || $('[data-review-success]', section)) {
          formWrap.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
        }
      }
      const grid = $('[data-review-grid]', section);
      if (!grid) return;
      const perPage = Number(grid.dataset.perPage) || 8;
      const more = $('[data-review-more]', section);
      let filter = 'all';
      let shown = perPage;
      const render = () => {
        const cards = $$('[data-review]', grid).filter((card) => filter === 'all' || card.dataset.hasPhoto === 'true');
        $$('[data-review]', grid).forEach((card) => card.classList.add('is-hidden'));
        cards.slice(0, shown).forEach((card) => card.classList.remove('is-hidden'));
        if (more) more.hidden = cards.length <= shown;
      };
      if (more) more.addEventListener('click', () => { shown += perPage; render(); });
      $$('[data-review-filter]', section).forEach((chip) => {
        chip.addEventListener('click', () => {
          filter = chip.dataset.reviewFilter;
          shown = perPage;
          $$('[data-review-filter]', section).forEach((c) => c.classList.toggle('is-active', c === chip));
          render();
        });
      });
      render();
    });
  };

  /* ───────────── Lightbox ───────────── */
  const Lightbox = {
    init() {
      this.el = $('[data-lightbox]');
      if (!this.el) return;
      this.img = $('.lightbox__img', this.el);
      this.el.addEventListener('click', (event) => {
        if (event.target.closest('[data-lightbox-prev]')) return this.step(-1);
        if (event.target.closest('[data-lightbox-next]')) return this.step(1);
        if (event.target.closest('[data-lightbox-close]') || event.target === this.el) this.close();
        return null;
      });
      document.addEventListener('keydown', (event) => {
        if (this.el.hidden) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-lightbox-src]');
        if (!trigger) return;
        event.preventDefault();
        const group = trigger.dataset.lightboxGroup;
        const scope = trigger.closest('section, product-info') || document;
        this.items = group ? $$(`[data-lightbox-group="${group}"]`) : $$('[data-lightbox-src]', scope).filter((el) => !el.closest('.is-hidden'));
        this.index = Math.max(0, this.items.indexOf(trigger));
        this.trigger = trigger;
        this.open();
      });
    },
    open() {
      this.el.hidden = false;
      lockScroll();
      this.show();
      $('[data-lightbox-close]', this.el).focus();
    },
    show() {
      const item = this.items[this.index];
      if (!item) return;
      this.img.src = item.dataset.lightboxSrc;
      this.el.classList.toggle('lightbox--single', this.items.length < 2);
    },
    step(direction) {
      if (!this.items || this.items.length < 2) return;
      this.index = (this.index + direction + this.items.length) % this.items.length;
      this.show();
    },
    close() {
      if (this.el.hidden) return;
      this.el.hidden = true;
      this.img.removeAttribute('src');
      unlockScroll();
      if (this.trigger) this.trigger.focus({ preventScroll: true });
    }
  };

  /* ───────────── Reveal on scroll ───────────── */
  let revealObserver;
  const initReveal = (scope = document) => {
    const items = $$('.reveal:not(.is-visible)', scope);
    if (!document.body.classList.contains('has-reveal') || reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    items.forEach((el, i) => {
      el.style.setProperty('--reveal-delay', `${Math.min(i % 4, 3) * 70}ms`);
      revealObserver.observe(el);
    });
  };

  /* ───────────── Login toggle ───────────── */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-login-toggle]');
    if (!toggle) return;
    const wrap = toggle.closest('[data-login]');
    if (!wrap) return;
    const login = $('[data-login-view]', wrap);
    const recover = $('[data-recover-view]', wrap);
    if (login && recover) {
      login.hidden = !login.hidden;
      recover.hidden = !recover.hidden;
    }
  });

  /* ───────────── Global openers ───────────── */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(opener);
      }
      return;
    }
    const cartLink = event.target.closest('[data-cart-open]');
    if (cartLink && theme.cartType === 'drawer') {
      const drawer = $('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(cartLink);
      }
    }
  });

  /* ───────────── Init ───────────── */
  const init = (scope = document) => {
    initReveal(scope);
    initReviews(scope);
  };

  const start = () => {
    Lightbox.init();
    init();
    if (window.location.hash === '#cart' || (window.location.search.includes('cart=open') && $('cart-drawer'))) $('cart-drawer').open();
    if ($('[data-cart-timer]')) CartTimer.start();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('.slider__slide');
    const track = slide ? slide.closest('[data-slider-track]') : null;
    if (track) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  });

  window.SpeelsnuitCart = Cart;
})();
